/**
 * The photos on the published menus (firebase /menus/<week>, read through v2/menu/:date), one
 * entry per image. The menu is keyed by stream; a virtual stream carries copies of the regular
 * streams' meals, and the chef's choice meals (date 'any') are listed under every stream, so a
 * photo is kept once with every stream, category, date and week it appears in.
 */
const SLOT_ORDER = ['plated', 'closeup', 'packaged'];

export const PHOTO_SLOTS = SLOT_ORDER;

export const CHEF_CHOICE_STREAM = 'chefs_choice';

// streams that only copy other streams' meals, kept out of the stream list and labels
export const HIDDEN_STREAMS = ['private', 'box'];

/** a photo's streams without the hidden ones */
export function visibleStreams(photo) {
  return photo.streams.filter(stream => !HIDDEN_STREAMS.includes(`${stream}`.toLowerCase()));
}

function unique(list) {
  return [...new Set(list.filter(value => value !== null && value !== undefined && value !== ''))];
}

function slotRank(slot) {
  const rank = SLOT_ORDER.indexOf(slot);
  return rank < 0 ? SLOT_ORDER.length : rank;
}

// the catalog publishes nutrients by meal size; the kitchen's checks (meals/nutrients) read the medium size
const NUTRIENT_SIZES = ['medium', 'small', 'large'];

/** a meal's nutrients by symbol for the medium size (or the first size there is), or a flat map as is */
export function catalogNutrients(nutrients) {
  if (!nutrients || typeof nutrients !== 'object') return null;
  const size = NUTRIENT_SIZES.find(name => nutrients[name] && typeof nutrients[name] === 'object');
  return size ? nutrients[size] : nutrients;
}

/** the catalog id without the legacy chef's choice suffix: '10191CC' → '10191' */
export function mealIdOf(id) {
  return `${id === undefined || id === null ? '' : id}`.replace(/CC$/i, '');
}

/**
 * [{date, menu}] (menu as v2/menu/:date returns it) → photos, in menu order: by week, delivery
 * date, stream, meal and slot. A week with no menu (null) adds nothing.
 */
export function collectPhotos(weeks) {
  const byUrl = new Map();
  for (const {date: week, menu} of weeks) {
    if (!menu) continue;
    for (const [streamKey, streamMenu] of Object.entries(menu)) {
      const days = (streamMenu && streamMenu.menu) || [];
      for (const day of days) {
        const meals = day.products || day.meals || [];
        for (const meal of meals) {
          const isChefChoice = day.date === 'any';
          const stream = isChefChoice ? (meal.stream || CHEF_CHOICE_STREAM) : streamKey;
          const category = meal.category || meal.tod || '';
          const date = isChefChoice ? 'any' : (meal.deliveryDate || day.date);
          for (const [slot, url] of Object.entries(meal.photos || {})) {
            if (!url) continue;
            let photo = byUrl.get(url);
            if (!photo) {
              photo = {
                key: url,
                url,
                slot,
                mealId: mealIdOf(meal.id),
                name: meal.name || '',
                sku: meal.sku || '',
                price: meal.price || null,
                ingredients: meal.ingredients || '',
                tags: unique([...(meal.tags || []), ...Object.values(meal.food_tags || {})]),
                // the food tag ids of the meal's ingredients, for the allergy filter; empty when unchecked
                tagIds: unique(Object.values(meal.ingredientTags || {}).flat().map(Number)),
                // the catalog's nutrients by symbol (medium size), for the meal preset filter
                nutrients: catalogNutrients(meal.nutrients),
                isChefChoice,
                streams: [],
                categories: [],
                dates: [],
                weeks: [],
              };
              byUrl.set(url, photo);
            }
            photo.streams = unique([...photo.streams, stream]);
            photo.categories = unique([...photo.categories, category]);
            photo.dates = unique([...photo.dates, date]);
            photo.weeks = unique([...photo.weeks, week]);
          }
        }
      }
    }
  }
  return [...byUrl.values()].sort(byMenuOrder);
}

function firstDate(photo) {
  // chef's choice photos (no date) sort after the dated meals of their week
  return photo.dates.filter(date => date !== 'any').sort()[0] || '9999';
}

function byMenuOrder(a, b) {
  return (a.weeks[0] || '').localeCompare(b.weeks[0] || '')
    || firstDate(a).localeCompare(firstDate(b))
    || a.name.localeCompare(b.name)
    || a.mealId.localeCompare(b.mealId)
    || slotRank(a.slot) - slotRank(b.slot);
}

/** the distinct values of a list field, in the order they appear */
export function distinctValues(photos, field) {
  return unique(photos.flatMap(photo => photo[field]));
}

function matchesAny(wanted, values) {
  return wanted.length === 0 || values.some(value => wanted.includes(value));
}

// the personal menu reads a filter's limits as a day's, over three meals (fed-lib personal-menu-meal-filters.js)
const MEALS_A_DAY = 3;

/**
 * A meal filter (the Meal Filter List page's presets, v2/meal-filter) as a check on a photo:
 * every nutrient target's limits met by the meal's amount scaled to a day, and none of the
 * filter's restrictions' tags among the ingredients. A target's symbol comes with the filter or
 * from the nutrient it names (symbolOf); one with neither, and a meal missing the nutrient, fail.
 */
export function mealFilterCheck(filter, restrictions, symbolOf = () => null) {
  const targets = ((filter && filter.nutrient_targets) || [])
    .filter(target => target.lower_limit || target.upper_limit)
    .map(target => ({
      symbol: target.nutrient_symbol || symbolOf(target.nutrient_id) || null,
      lowerLimit: Number(target.lower_limit) || null,
      upperLimit: Number(target.upper_limit) || null,
    }));
  return {targets, excludeTagIds: restrictedTagIds(restrictions, (filter && filter.restrictions) || [])};
}

function meetsTargets(photo, targets) {
  return targets.every(({symbol, lowerLimit, upperLimit}) => {
    const amountInMeal = symbol && photo.nutrients ? photo.nutrients[symbol] : undefined;
    if (amountInMeal === undefined || amountInMeal === null) return false;
    const amount = Number(amountInMeal) * MEALS_A_DAY;
    return !(lowerLimit && amount < lowerLimit) && !(upperLimit && amount > upperLimit);
  });
}

/**
 * photos in the chosen streams, categories and slots (an empty choice is all of them), whose
 * name, id or ingredients contain the search text, leaving out any meal with an ingredient
 * tagged with one of excludeTagIds (an allergy's restricted tags; a meal with no ingredient
 * tags cannot be checked and stays) and, with a mealFilter (mealFilterCheck), any meal that
 * fails it
 */
export function filterPhotos(photos, {streams = [], categories = [], slots = [], search = '', excludeTagIds = [], mealFilter = null} = {}) {
  const text = `${search || ''}`.trim().toLowerCase();
  const excluded = new Set([...excludeTagIds, ...((mealFilter && mealFilter.excludeTagIds) || [])].map(Number));
  return photos.filter(photo => matchesAny(streams, photo.streams)
    && matchesAny(categories, photo.categories)
    && matchesAny(slots, [photo.slot])
    && !photo.tagIds.some(tagId => excluded.has(tagId))
    && (!mealFilter || meetsTargets(photo, mealFilter.targets))
    && (!text || `${photo.name} m${photo.mealId} ${photo.sku} ${photo.ingredients}`.toLowerCase().includes(text)));
}

/** the restricted tag ids of the chosen restrictions, from the store's restrictions map */
export function restrictedTagIds(restrictions, restrictionIds) {
  return unique(restrictionIds.flatMap(id => ((restrictions || {})[id] || {}).restrictedTags || []).map(Number));
}

const EXTENSIONS = {'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/avif': 'avif', 'image/gif': 'gif'};

/** 'chicken-tikka-M5001-plated.jpg': a file name for a downloaded photo */
export function photoFileName(photo, contentType = 'image/jpeg') {
  const slug = `${photo.name || 'meal'}`.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'meal';
  const extension = EXTENSIONS[`${contentType || ''}`.split(';')[0].trim()] || 'jpg';
  return `${slug}-M${photo.mealId}-${photo.slot}.${extension}`;
}

/** one line with the meal's details and the image url, for pasting into a message */
export function photoDetailsLine(photo, url = photo.url) {
  const dates = photo.dates.map(date => (date === 'any' ? 'chef\'s choice' : date));
  return [
    `${photo.name} (M${photo.mealId})`,
    photo.sku,
    photo.slot,
    visibleStreams(photo).join('/'),
    photo.categories.join('/'),
    dates.join(', '),
    url,
  ].filter(part => part !== '' && part !== null && part !== undefined).join(' · ');
}
